import { Database, ShieldCheck, Smartphone } from "lucide-react";
import type { ReactNode } from "react";
import heroSmall from "../../assets/signin-hero-720.webp";
import heroLarge from "../../assets/signin-hero-1448.webp";
import { Logo } from "../../components/ui/logo";

const highlights = [
  { icon: ShieldCheck, text: "Each person sees only what their role allows" },
  { icon: Smartphone, text: "Large, clear screens that work on any phone" },
  { icon: Database, text: "Every school's data is encrypted and kept apart" },
];

/**
 * The photo is decorative (alt=""). Both layouts choose the file by screen width, not pixel
 * density, so a phone only ever downloads the 28 KB copy (even on a 3x screen) and a desktop
 * only the full photo. Each device fetches one file, shared by both layouts.
 */
function HeroImage({ className }: { className: string }) {
  return (
    <picture className="contents">
      <source media="(min-width: 1024px)" srcSet={heroLarge} />
      <img src={heroSmall} alt="" decoding="async" className={className} />
    </picture>
  );
}

/** Who the sign-in page is for: a school's people, or Eduleo's own team (the console). */
export type SignInAudience = "school" | "team";

/** Desktop: full-height photo beside the form, with the product message over a dark fade. */
export function SignInHeroPanel({ audience = "school" }: { audience?: SignInAudience }) {
  return (
    <aside className="relative hidden overflow-hidden lg:block">
      <HeroImage className="absolute inset-0 size-full object-cover object-[10%_center]" />
      <div
        aria-hidden
        className="absolute inset-0 bg-linear-to-t from-navy/95 from-15% via-navy/70 via-50% to-navy/0 to-80%"
      />
      {/* Content sits at the bottom so the photo's own labels at the top stay clear. */}
      <div className="relative flex h-full flex-col justify-end p-10 xl:p-12">
        <div className="max-w-lg text-white">
          <span className="mb-6 inline-block rounded-xl bg-surface/90 px-3 py-2 shadow-sm backdrop-blur-sm">
            <Logo showTagline />
          </span>
          <h1 className="font-extrabold text-3xl leading-tight">
            Run your school from one place, on any phone.
          </h1>
          <ul className="mt-6 flex flex-col gap-3">
            {highlights.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-lg text-white/90">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <Icon aria-hidden className="size-5" strokeWidth={1.5} />
                </span>
                {text}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-white/80">
            {audience === "team"
              ? "Need help signing in? Ask a super admin on your team."
              : "Need help signing in? Your school office can help."}
          </p>
        </div>
      </div>
    </aside>
  );
}

/** Phones and tablets: a short photo banner above the form, with the logo on it. */
export function SignInHeroBanner() {
  return (
    <div className="relative mb-6 h-40 overflow-hidden rounded-2xl shadow-sm sm:h-48 lg:hidden">
      <HeroImage className="size-full object-cover object-[30%_42%]" />
      <div aria-hidden className="absolute inset-0 bg-linear-to-t from-navy/50 to-navy/0" />
      <span className="absolute bottom-3 left-3 rounded-xl bg-surface/90 px-3 py-2 shadow-sm backdrop-blur-sm">
        <Logo />
      </span>
    </div>
  );
}

/** The sign-in page frame: hero beside (desktop) or above (phones) a card with the form. */
export function SignInLayout({
  audience = "school",
  children,
  footer,
}: {
  audience?: SignInAudience;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[1.1fr_1fr]">
      <SignInHeroPanel audience={audience} />

      <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-6 sm:px-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <SignInHeroBanner />
          <div className="rounded-xl border border-edge bg-surface p-6 shadow-sm sm:p-8">
            {children}
          </div>
          {footer}
        </div>
      </main>
    </div>
  );
}
